import { useState } from "react";
import { motion } from "framer-motion";
import { ArrowUpRight, BookOpen, ChevronLeft, ChevronRight } from "lucide-react";

import Card from "../../Card/Card";
import Container from "../../Container/Container";
import MediaTile from "../../MediaTile/MediaTile";
import SectionTitle from "../../SectionTitle/SectionTitle";
import { fadeUp, staggerContainer, viewportOnce } from "../../../Utils/animations";
import { formatDate } from "../../../Utils/helpers";
import { knowledgeHub } from "../../../Data/knowledgeHubData";

import "../../../Style/home/knowledgeHub.css";

function KnowledgeHub() {
  const [activeIndex, setActiveIndex] = useState(0);
  const featuredArticles = knowledgeHub.slice(0, 3);

  const handleReadMore = (e) => {
    e.preventDefault();
    window.history.pushState({}, "", "/blog");
    window.dispatchEvent(new Event("popstate"));
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const nextSlide = () => {
    setActiveIndex((prev) => (prev + 1) % featuredArticles.length);
  };

  const prevSlide = () => {
    setActiveIndex((prev) => (prev - 1 + featuredArticles.length) % featuredArticles.length);
  };

  return (
    <section id="knowledge-hub" className="section section--tint knowledge-hub">
      <Container>
        <div className="knowledge-hub__header-row">
          <SectionTitle
            eyebrow="Knowledge Hub"
            title="Latest insights"
            highlight="& articles."
          />

          {/* Mobile-only Carousel Arrow Controls */}
          <div className="knowledge-hub__mobile-arrows">
            <button
              type="button"
              className="knowledge-hub__arrow-btn"
              onClick={prevSlide}
              aria-label="Previous article"
            >
              <ChevronLeft size={18} />
            </button>
            <button
              type="button"
              className="knowledge-hub__arrow-btn"
              onClick={nextSlide}
              aria-label="Next article"
            >
              <ChevronRight size={18} />
            </button>
          </div>
        </div>

        {/* Carousel / Grid Container */}
        <div className="knowledge-hub__carousel-wrapper">
          <motion.div
            className="card-grid card-grid--3 knowledge-hub__grid"
            variants={staggerContainer(0.08)}
            initial="hidden"
            whileInView="show"
            viewport={viewportOnce}
            style={{
              "--active-index": activeIndex,
            }}
          >
            {featuredArticles.map((post, idx) => (
              <Card
                key={post.id}
                interactive
                variants={fadeUp()}
                className={`knowledge-hub__card ${idx === activeIndex ? "is-active" : ""}`}
              >
                <div className="knowledge-hub__media">
                  <MediaTile tone={post.tone} icon={<BookOpen size={22} />} />
                </div>
                <div className="knowledge-hub__meta">
                  <span>{post.category}</span>
                  <span>{formatDate(post.date)}</span>
                </div>
                <h3 className="card__title mt-2">{post.title}</h3>
                <p className="card__text">{post.excerpt}</p>
                <a
                  href="/blog"
                  onClick={handleReadMore}
                  className="link-arrow knowledge-hub__link"
                >
                  Read More <ArrowUpRight size={16} />
                </a>
              </Card>
            ))}
          </motion.div>
        </div>

        {/* Mobile Pagination Dots */}
        <div className="knowledge-hub__dots">
          {featuredArticles.map((_, dotIdx) => (
            <button
              key={dotIdx}
              type="button"
              className={`knowledge-hub__dot ${dotIdx === activeIndex ? "knowledge-hub__dot--active" : ""}`}
              onClick={() => setActiveIndex(dotIdx)}
              aria-label={`Go to slide ${dotIdx + 1}`}
            />
          ))}
        </div>
      </Container>
    </section>
  );
}

export default KnowledgeHub;